import React, { useState } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import { createComment } from '../../store/slices/commentsSlice';
import Button from '../UI/Button';

interface CommentFormProps {
  entityType: 'project' | 'projectFile';
  entityId: string;
  parentId?: string;
  onSuccess?: () => void;
  showRating?: boolean;
}

const CommentForm: React.FC<CommentFormProps> = ({
  entityType,
  entityId,
  parentId,
  onSuccess,
  showRating = false,
}) => {
  const dispatch = useAppDispatch();
  const [content, setContent] = useState('');
  const [rating, setRating] = useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await dispatch(
        createComment({
          entityType,
          entityId,
          content: content.trim(),
          parentId,
          rating: showRating ? rating : undefined,
        })
      );
      setContent('');
      setRating(undefined);
      onSuccess?.();
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[var(--color-default-background)] border border-[var(--color-neutral-border)] rounded-lg p-4">
      {showRating && !parentId && (
        <div className="mb-3">
          <label className="block text-sm font-medium text-[var(--color-default-font)] mb-2">
            Rating (0-6 stars)
          </label>
          <div className="flex items-center gap-2">
            {[0, 1, 2, 3, 4, 5, 6].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className={`text-2xl transition-colors ${
                  rating !== undefined && rating >= star
                    ? 'text-[var(--color-warning-500)]'
                    : 'text-[var(--color-neutral-300)] hover:text-[var(--color-warning-400)]'
                }`}
                aria-label={`${star} stars`}
              >
                ★
              </button>
            ))}
            {rating !== undefined && (
              <span className="text-sm text-[var(--color-subtext-color)] ml-2">{rating} stars</span>
            )}
            {rating !== undefined && (
              <button
                type="button"
                onClick={() => setRating(undefined)}
                className="text-sm text-[var(--color-subtext-color)] hover:text-[var(--color-default-font)] ml-2"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={parentId ? 'Write a reply...' : 'Write a comment...'}
        className="w-full px-3 py-2 border border-[var(--color-neutral-border)] rounded-md bg-[var(--color-default-background)] text-[var(--color-default-font)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-500)] mb-3"
        rows={4}
        required
        aria-label={parentId ? 'Reply text' : 'Comment text'}
      />
      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          disabled={!content.trim() || isSubmitting}
          isLoading={isSubmitting}
        >
          {parentId ? 'Post Reply' : 'Post Comment'}
        </Button>
      </div>
    </form>
  );
};

export default CommentForm;
